import { useState } from 'react'
import {
  ArrowRight,
  Cable,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleX,
  LoaderCircle,
} from 'lucide-react'
import { Link } from 'react-router-dom'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type {
  CredentialStatusResponse,
  CredentialValidationResponse,
} from './types'
import { TestPatientDemographics } from './TestPatientDemographics'

interface EcwConnectionSheetProps {
  credential: CredentialStatusResponse
  validation?: CredentialValidationResponse
  isLoading: boolean
  isError: boolean
  isStarting: boolean
  isApproving: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onRetry: () => void
  onValidate: () => void
  onApprove: () => void
}

function stateCopy(
  validation: CredentialValidationResponse | undefined,
  isLoading: boolean,
  isError: boolean
) {
  if (isLoading) {
    return {
      label: 'Checking',
      title: 'Checking validation status',
      description: 'Loading the latest validation session.',
      tone: 'working' as const,
    }
  }
  if (isError) {
    return {
      label: 'Unavailable',
      title: 'Could not load validation status',
      description: 'Try loading the latest validation status again.',
      tone: 'failed' as const,
    }
  }
  switch (validation?.state) {
    case 'pending':
    case 'running':
      return {
        label: 'Validating',
        title: 'Validating connection',
        description:
          'Careforce is signing in and opening the configured test patient.',
        tone: 'working' as const,
      }
    case 'passed':
      return {
        label: 'Validation passed',
        title: 'Validation passed',
        description: 'The connection and test patient are ready for approval.',
        tone: 'success' as const,
      }
    case 'failed':
      return {
        label: 'Validation failed',
        title: 'Validation failed',
        description: 'Review the session, then try again when ready.',
        tone: 'failed' as const,
      }
    default:
      return {
        label: 'Submitted',
        title: 'Connection details submitted',
        description:
          'Validate the credentials and configured test patient before approving this connection.',
        tone: 'idle' as const,
      }
  }
}

export function EcwConnectionSheet({
  credential,
  validation,
  isLoading,
  isError,
  isStarting,
  isApproving,
  open,
  onOpenChange,
  onRetry,
  onValidate,
  onApprove,
}: EcwConnectionSheetProps) {
  const [metadataOpen, setMetadataOpen] = useState(false)
  const copy = stateCopy(validation, isLoading, isError)
  const working = copy.tone === 'working' || isStarting || isApproving

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="connection-sheet"
        className="bg-neutral-ui-bg w-full max-w-none gap-0 overflow-y-auto rounded-l-xl p-0 sm:max-w-lg"
        onOpenAutoFocus={(event) => event.preventDefault()}>
        <SheetHeader className="bg-neutral-ui-bg sticky top-0 z-10 border-b border-border">
          <div className="flex items-center gap-2">
            <Cable className="size-5" />
            <SheetTitle>eClinicalWorks</SheetTitle>
          </div>
          <SheetDescription>{copy.label}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-6 p-4">
          <div className="flex items-start gap-3 rounded-xl bg-muted/35 p-4">
            {working ? (
              <LoaderCircle className="mt-0.5 size-5 shrink-0 animate-spin text-primary-solid" />
            ) : copy.tone === 'success' ? (
              <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success-solid" />
            ) : copy.tone === 'failed' ? (
              <CircleX className="mt-0.5 size-5 shrink-0 text-destructive" />
            ) : (
              <Cable className="mt-0.5 size-5 shrink-0 text-primary" />
            )}
            <div>
              <h3 className="font-medium text-primary-high-contrast">
                {copy.title}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {copy.description}
              </p>
              {validation?.session_id && (
                <Link
                  to={`/sessions/${validation.session_id}`}
                  className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-solid hover:underline">
                  View validation session
                  <ArrowRight className="size-3.5" />
                </Link>
              )}
            </div>
          </div>

          <TestPatientDemographics validation={validation} />

          <Collapsible open={metadataOpen} onOpenChange={setMetadataOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-muted-foreground underline-offset-4 hover:text-primary-high-contrast hover:underline">
                {metadataOpen ? <ChevronDown /> : <ChevronRight />}
                {metadataOpen
                  ? 'Hide additional metadata'
                  : 'Show additional metadata'}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Submitted version</dt>
                <dd className="text-primary-high-contrast">
                  Version {credential.candidate_version}
                </dd>
                <dt className="text-muted-foreground">Current version</dt>
                <dd className="text-primary-high-contrast">
                  {credential.active_version === null
                    ? 'Not connected'
                    : `Version ${credential.active_version}`}
                </dd>
              </dl>
            </CollapsibleContent>
          </Collapsible>
        </div>

        <SheetFooter className="border-t border-border bg-neutral-ui-bg">
          {isError && <Button onClick={onRetry}>Retry</Button>}
          {(validation?.state === 'not_started' ||
            validation?.state === 'failed') &&
            !isError && (
              <Button
                data-testid="validate-credentials"
                disabled={isStarting}
                onClick={onValidate}>
                {isStarting && <LoaderCircle className="animate-spin" />}
                {validation.state === 'failed'
                  ? 'Try validation again'
                  : 'Validate connection'}
              </Button>
            )}
          {validation?.state === 'passed' && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="open-approval" disabled={isApproving}>
                  {isApproving && <LoaderCircle className="animate-spin" />}
                  Approve connection
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Approve this connection?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Careforce will begin using these connection details for this
                    organization.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="confirm-approval"
                    onClick={onApprove}>
                    Approve connection
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {(isLoading ||
            validation?.state === 'pending' ||
            validation?.state === 'running') && (
            <Button disabled>
              <LoaderCircle className="animate-spin" />
              Validating…
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
